import {
    useContext,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    type KeyboardEvent,
    type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/Button";
import { OverlayOwnerContext, focusableElements } from "@/components/overlay";

export interface MenuProps {
    label: string;
    trigger: ReactNode;
    align?: "left" | "center" | "right";
    active?: boolean;
    hover?: boolean;
    children: (close: () => void) => ReactNode;
}

export const Menu = ({
    label,
    trigger,
    align = "left",
    active = false,
    hover = true,
    children,
}: MenuProps) => {
    const id = useId();
    const owner = useContext(OverlayOwnerContext);
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const [position, setPosition] = useState({
        left: 0,
        top: 0,
        maxHeight: 400,
    });
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const button = useRef<HTMLButtonElement>(null);
    const panel = useRef<HTMLElement>(null);

    const close = (restoreFocus = true): void => {
        if (!open || closing) {
            return;
        }
        setClosing(true);
        if (restoreFocus) {
            button.current?.focus();
        }
        closeTimer.current = setTimeout(() => {
            setOpen(false);
            setClosing(false);
        }, 160);
    };
    useEffect(
        () => () => {
            if (closeTimer.current) {
                clearTimeout(closeTimer.current);
            }
            if (hoverTimer.current) {
                clearTimeout(hoverTimer.current);
            }
        },
        []
    );
    useEffect(() => {
        if (open) {
            return;
        }
        setClosing(false);
    }, [location.key]);
    useLayoutEffect(() => {
        if (!open || !button.current || !panel.current) {
            return;
        }
        const anchor = button.current.getBoundingClientRect();
        const width = panel.current.getBoundingClientRect().width;
        const below = window.innerHeight - anchor.bottom - 16;
        const alignedLeft =
            align === "right"
                ? anchor.right - width
                : align === "center"
                  ? anchor.left + anchor.width / 2 - width / 2
                  : anchor.left;
        setPosition({
            left: Math.max(
                8,
                Math.min(alignedLeft, window.innerWidth - width - 8)
            ),
            top: anchor.bottom + 6,
            maxHeight: Math.max(120, below),
        });
        focusableElements(panel.current)[0]?.focus();
    }, [align, open]);
    useEffect(() => {
        if (!open) {
            return;
        }
        const outside = (event: Event): void => {
            const target = event.target as Node;
            if (
                !panel.current?.contains(target) &&
                !button.current?.contains(target)
            ) {
                close(false);
            }
        };
        const escape = (event: globalThis.KeyboardEvent): void => {
            if (event.key === "Escape" && !event.defaultPrevented) {
                event.preventDefault();
                close();
            }
        };
        const resize = (): void => close(false);
        document.addEventListener("pointerdown", outside);
        document.addEventListener("focusin", outside);
        document.addEventListener("keydown", escape);
        window.addEventListener("resize", resize);
        return () => {
            document.removeEventListener("pointerdown", outside);
            document.removeEventListener("focusin", outside);
            document.removeEventListener("keydown", escape);
            window.removeEventListener("resize", resize);
        };
    }, [open]);
    const keyboard = (event: KeyboardEvent<HTMLElement>): void => {
        if (
            !panel.current ||
            !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
        ) {
            return;
        }
        event.preventDefault();
        const controls = focusableElements(panel.current);
        const current = controls.indexOf(document.activeElement as HTMLElement);
        const next =
            event.key === "Home"
                ? 0
                : event.key === "End"
                  ? controls.length - 1
                  : (current +
                        (event.key === "ArrowDown" ? 1 : -1) +
                        controls.length) %
                    controls.length;
        controls[next]?.focus();
    };
    const openOnHover = (): void => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
        }
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
        }
        setClosing(false);
        hoverTimer.current = setTimeout(() => setOpen(true), 80);
    };
    const closeOnHover = (): void => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current);
        }
        hoverTimer.current = setTimeout(() => close(false), 120);
    };
    const triggerClass =
        "shrink-0 !p-0 !text-xs transition-colors hover:!font-semibold hover:!text-primary dark:hover:!text-primary-300 " +
        (active
            ? "!bg-primary/10 !text-primary dark:!text-primary-300"
            : "!text-slate-700 dark:!text-zinc-200");
    const panelClass =
        "select-dropdown select-dropdown--open-bottom fixed z-[100100] w-80 max-w-[calc(100vw-1rem)] overflow-x-hidden overflow-y-auto overscroll-contain rounded-lg border border-slate-200 bg-white p-2 shadow-xl dark:border-white/15 dark:bg-dark-box" +
        (closing ? " select-dropdown--closing-bottom" : "");
    return (
        <div
            className="flex justify-center"
            onMouseEnter={hover ? openOnHover : undefined}
            onMouseLeave={hover ? closeOnHover : undefined}
        >
            <Button
                ref={button}
                variant="ghost"
                size="sm"
                aria-label={label}
                aria-expanded={open}
                aria-controls={open ? id : undefined}
                aria-current={active ? "true" : undefined}
                onClick={() => {
                    if (closing) {
                        return;
                    }
                    setOpen((previous) => !previous);
                }}
                onKeyDown={(event) => {
                    if (event.key === "ArrowDown") {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
                className={triggerClass}
            >
                {trigger}
            </Button>
            {(open || closing) &&
                createPortal(
                    <nav
                        ref={panel}
                        id={id}
                        aria-label={label}
                        data-overlay-owner={owner || undefined}
                        onKeyDown={keyboard}
                        onMouseEnter={hover ? openOnHover : undefined}
                        onMouseLeave={hover ? closeOnHover : undefined}
                        className={panelClass}
                        style={position}
                    >
                        {children(() => close())}
                    </nav>,
                    document.body
                )}
        </div>
    );
};

export default Menu;
